import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  ListItemIcon,
  Menu,
  MenuItem,
  OutlinedInput,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Form, Formik, getIn, useFormikContext } from 'formik';
import debounce from 'lodash/debounce';
import * as Yup from 'yup';
import { Add, Download, Edit, ArticleTwoTone, Close, MoreVert, Delete, Restore } from '@mui/icons-material';
import { useTranslationMessages } from 'lib/hooks/useTranslationMessages';

import { Experience, UseResumeDataQuery, MilitaryExperience } from 'lib/hooks/useResumeDataQuery';
import TextInputField from 'components/inputs/TextInputField';
import PhoneInputField from 'components/inputs/PhoneInputField';
import ExperienceWalletSkillsAutocomplete from 'components/forms/experienceWallet/ExperienceWalletSkillsAutocomplete';
import ExperienceWalletForm from 'components/forms/experienceWallet/ExperienceWalletForm';
import { sortExperiences } from 'components/resume-builder/utils';
import ReferencesSection from 'components/resume-builder/ReferencesSection';
import { FormattedMessage } from 'react-intl';
import { BRANCH_OPTIONS, RANKS_BY_BRANCH } from 'constants/militaryOptions';
import { EDUCATION_LEVEL_BY_ID, EDUCATION_CREDENTIAL_LABEL_IDS } from 'constants/educationLevelOptions';
import { lcwTypeLabels } from 'components/forms/experienceWallet/ExperienceWalletDrawer';

const emptyExperience = {
  id: '',
  type: '',
  level: null,
  field: null,
  fields: [],
  skills: [],
  title: '',
  employer_provider: '',
  start_date: null,
  end_date: null,
  comments: null,
  is_current: false,
};

const emptyMilitaryExperience = {
  id: '',
  type: 'employment',
  experienceCategory: 'military',
  field: null,
  fields: [],
  skills: [],
  title: '',
  employer_provider: '',
  branch: '',
  rank: '',
  start_date: null,
  end_date: null,
  comments: null,
  is_current: false,
};

const EDUCATION_TYPE_LABEL_BY_KEY = new Map<string, string>([
  ['assessment', 'Assessment'],
  ['badge', 'Badge'],
  // Backend CredentialTypeEnum.openbadge covers both LER "OpenBadgeCredential" and
  // "AchievementCredential" types — both are badges to the user.
  ['openbadge', 'Badge'],
  // Defensive: in case the backend ever sends this raw/unnormalized instead of openbadge.
  ['achievement', 'Achievement'],
  ['AchievementCredential', 'Achievement'],
  ['course', 'Course'],
  ['militaryCourse', 'Military Course'],
  ['certificate', 'Certificate'],
  ['certification', 'Certification'],
  ['license', 'License'],
  ['apperenticeshipRTI', 'Apprenticeship Related Technical Instruction (RTI)'],
  ['degree', 'Education Degree'],
]);

export function EditableCard({
  title,
  subtitle,
  chipLabel,
  onEdit,
  onDelete,
  onRestore,
}: {
  title: string;
  subtitle: string;
  chipLabel?: string;
  onEdit?: () => void;
  onDelete?: () => void;
  onRestore?: () => void;
}) {
  const { t } = useTranslationMessages();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Paper
      variant="outlined"
      sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', opacity: onRestore ? 0.6 : 1 }}
    >
      <Box flexGrow={1}>
        <Box display="flex">
          <Typography fontWeight="bold" variant="body1">
            {title}
          </Typography>
          {chipLabel && <Chip label={chipLabel} size="small" color="secondary" sx={{ ml: 1 }} />}
        </Box>
        <Typography variant="subtitle2" color="textSecondary">
          {subtitle}
        </Typography>
      </Box>

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
          <Tooltip title={t('createResume.row.actions')}>
            <IconButton
              onClick={handleClick}
              size="small"
              aria-controls={open ? 'actions-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
            >
              <MoreVert sx={{ width: 24, height: 24 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Menu
          anchorEl={anchorEl}
          id="actions-menu"
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {onEdit && (
            <MenuItem onClick={onEdit} id="edit-experience-resume-button">
              <ListItemIcon>
                <Edit fontSize="small" />
              </ListItemIcon>
              {t('createResume.row.edit')}
            </MenuItem>
          )}
          {onDelete && (
            <MenuItem onClick={onDelete} id="delete-resume-experience-button">
              <ListItemIcon>
                <Delete fontSize="small" />
              </ListItemIcon>
              {t('createResume.row.delete')}
            </MenuItem>
          )}
          {onRestore && (
            <MenuItem onClick={onRestore} id="restore-resume-experience-button">
              <ListItemIcon>
                <Restore fontSize="small" />
              </ListItemIcon>
              {t('createResume.row.addToResume')}
            </MenuItem>
          )}
        </Menu>
      </Box>
    </Paper>
  );
}

const FormStateUpdater = ({
  formValues,
  updateParentState,
}: {
  formValues: UseResumeDataQuery['data'];
  updateParentState: (newValues: UseResumeDataQuery['data']) => void;
}) => {
  const { values } = useFormikContext<UseResumeDataQuery['data']>();

  // Reference to track if the updater has run at least once
  const hasUpdatedOnceRef = useRef(false);

  // Use refs for formValues and callbacks so they don't trigger useEffect re-runs
  const formValuesRef = useRef(formValues);
  formValuesRef.current = formValues;

  const updateParentStateRef = useRef(updateParentState);
  updateParentStateRef.current = updateParentState;

  const showMilitaryStatusRef = useRef(formValues.header.showMilitaryStatus);
  showMilitaryStatusRef.current = formValues.header.showMilitaryStatus;

  // Create debouncedUpdate once — stable reference, no re-creation on renders
  const debouncedUpdate = useRef(
    debounce((newValues: UseResumeDataQuery['data']) => {
      updateParentStateRef.current(newValues);
    }, 250)
  ).current;

  useEffect(() => {
    const currentFormValues = formValuesRef.current;

    if (!hasUpdatedOnceRef.current) {
      const initialData = {
        ...currentFormValues,
        ...values,
      };
      debouncedUpdate(initialData);
      hasUpdatedOnceRef.current = true;
    } else if (values.header !== currentFormValues.header) {
      // Preserve showMilitaryStatus from parent state via ref
      const updatedHeader =
        showMilitaryStatusRef.current === values.header.showMilitaryStatus
          ? values.header
          : { ...values.header, showMilitaryStatus: showMilitaryStatusRef.current };
      debouncedUpdate({ ...currentFormValues, header: updatedHeader });
    }

    return () => {
      debouncedUpdate.cancel();
    };
  }, [values, debouncedUpdate]);

  return null;
};

const EDITING_ITEM_INITIAL_STATE = {
  type: 'work',
  index: -1,
  item: null,
};

function parseMMYYYY(dateStr: string | null | undefined): number {
  if (!dateStr) return 0;
  const match = dateStr.match(/^(\d{2})\/(\d{4})$/);
  if (match) return new Date(parseInt(match[2]), parseInt(match[1]) - 1).getTime();
  const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-\d{2}/);
  if (isoMatch) return new Date(parseInt(isoMatch[1]), parseInt(isoMatch[2]) - 1).getTime();
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? 0 : d.getTime();
}

function compareExperienceDates(a: Experience | MilitaryExperience, b: Experience | MilitaryExperience): number {
  const aIsCurrent = a.is_current === true || a.end_date === null || a.end_date === undefined;
  const bIsCurrent = b.is_current === true || b.end_date === null || b.end_date === undefined;
  if (aIsCurrent !== bIsCurrent) return aIsCurrent ? -1 : 1;
  const endDateDiff = parseMMYYYY(b.end_date) - parseMMYYYY(a.end_date);
  if (endDateDiff !== 0) return endDateDiff;
  return parseMMYYYY(b.start_date) - parseMMYYYY(a.start_date);
}

function computeSkillsFromSections(
  sections: UseResumeDataQuery['data']['sections'],
  includeMilitary: boolean
): { name: string; id: string }[] {
  const workSection = sections.find((s) => s.type === 'work_experience');
  const othersSection = sections.find((s) => s.type === 'others');
  const militarySection = sections.find((s) => s.type === 'military_experience');
  const experienceItems = [
    ...((workSection?.items as Experience[]) ?? []).filter((exp) => !exp.hidden),
    ...((othersSection?.items as Experience[]) ?? []).filter((exp) => !exp.hidden),
    ...((militarySection?.items as MilitaryExperience[]) ?? []).filter((mil) => !mil.hidden),
  ];
  return Array.from(
    new Map(
      experienceItems.flatMap((exp) => exp.skills.map((skill: { name: string; id: string }) => [skill.name, skill]))
    ).values()
  ).slice(0, 20);
}

function ResumeEditor({
  formValues,
  handleDownload,
  updateFormValues,
  resumeInitialValues,
}: {
  formValues: UseResumeDataQuery['data'];
  handleDownload: () => void;
  updateFormValues: (newValues: UseResumeDataQuery['data']) => void;
  resumeInitialValues: UseResumeDataQuery['data'];
}) {
  const { t } = useTranslationMessages();
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<{
    type: string;
    index: number;
    item: Experience | MilitaryExperience | null;
  }>(EDITING_ITEM_INITIAL_STATE);
  const [experienceModalOpen, setExperienceModalOpen] = useState(false);

  const closeModal = () => {
    setExperienceModalOpen(false);
    setEditingItem(EDITING_ITEM_INITIAL_STATE);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFormSubmit = (experienceFormValues: Record<string, any>) => {
    const isEdit = editingItem.index !== -1;
    const isMilitaryItem = editingItem.type === 'military' && experienceFormValues.experienceCategory !== 'civilian';
    const isEducation = editingItem.type === 'education';
    const sectionIndex = isMilitaryItem ? 2 : isEducation ? 4 : 3;

    const prev = structuredClone(formValues);
    if (isMilitaryItem) {
      const milItem: MilitaryExperience = {
        title: experienceFormValues.field?.moc_title || experienceFormValues.title || '',
        branch: experienceFormValues.branch || experienceFormValues.branch_of_service || '',
        rank: experienceFormValues.rank || '',
        skills: experienceFormValues.skills || [],
        start_date: experienceFormValues.start_date || '',
        end_date: experienceFormValues.is_current ? null : experienceFormValues.end_date ?? null,
        comments: experienceFormValues.comments ?? null,
        is_current: experienceFormValues.is_current ?? false,
        field: experienceFormValues.field ?? null,
      };
      if (isEdit) {
        (prev.sections[sectionIndex].items as MilitaryExperience[])[editingItem.index] = milItem;
      } else {
        (prev.sections[sectionIndex].items as MilitaryExperience[]).push(milItem);
      }
    } else {
      if (isEdit) {
        (prev.sections[sectionIndex].items as Experience[])[editingItem.index] =
          experienceFormValues as unknown as Experience;
      } else {
        (prev.sections[sectionIndex].items as Experience[]).push(experienceFormValues as unknown as Experience);
        prev.sections[sectionIndex].items = sortExperiences(prev.sections[sectionIndex].items as Experience[]);
      }
    }
    prev.sections[1].items = computeSkillsFromSections(prev.sections, prev.header.showMilitaryStatus);
    updateFormValues(prev);
    closeModal();
  };

  // Create validation schemas with translated messages
  const headerSchema = Yup.object().shape({
    header: Yup.object().shape({
      name: Yup.string().required(t('createResume.validation.nameRequired')),
      email: Yup.string().email(t('createResume.validation.emailInvalid')),
      phone_number: Yup.string(),
      url: Yup.string().url(t('createResume.validation.urlInvalid')),
      location: Yup.string(),
    }),
  });

  const summarySchema = Yup.object().shape({
    summary: Yup.string().max(500, t('createResume.validation.summaryTooLong')),
  });

  function handleEditExperience(index: number, type: string) {
    const sectionIndex = type === 'work' ? 3 : type === 'military' ? 2 : 4;
    setExperienceModalOpen(true);
    setEditingItem({
      index,
      type,
      item: (formValues.sections[sectionIndex].items as (Experience | MilitaryExperience)[])[index],
    });
  }

  return (
    <Box>
      <Typography variant="body2" color="error" sx={{ mb: 2 }}>
        <strong>{t('createResume.header.note')}</strong>
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
        }}
      >
        <Typography variant="h1">
          <ArticleTwoTone sx={{ fontSize: 24, position: 'relative', top: 2 }} /> {t('createResume.header.title')}
        </Typography>

        <Button startIcon={<Download />} variant="contained" onClick={handleDownload} id="download-resume-button">
          {t('createResume.header.downloadButton')}
        </Button>
      </Box>

      <Typography gutterBottom sx={{ mb: 2 }}>
        <FormattedMessage
          id="createResume.header.description"
          values={
            {
              strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
            } as any // eslint-disable-line @typescript-eslint/no-explicit-any
          }
        />
      </Typography>

      <Formik
        initialValues={{ header: resumeInitialValues.header }}
        validationSchema={headerSchema}
        onSubmit={() => {}}
      >
        {({ errors, touched }) => (
          <Form>
            <FormStateUpdater formValues={formValues} updateParentState={updateFormValues} />
            {/* Header Section */}
            <Box mb={2}>
              <Typography variant="h4" component="h2">
                {t('createResume.contact.title')}
              </Typography>

              <Typography component="p" variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                {t('createResume.contact.description')}
              </Typography>

              <Grid container columnSpacing={2}>
                <Grid item xs={12} md={6}>
                  <TextInputField
                    name="header.name"
                    label={t('createResume.contact.fieldsName')}
                    error={getIn(touched, 'header.name') && getIn(errors, 'header.name')}
                    id="your-name-resume-field"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <PhoneInputField
                    name="header.phone_number"
                    label={t('createResume.contact.fieldsPhone')}
                    error={getIn(touched, 'header.phone_number') && getIn(errors, 'header.phone_number')}
                    id="phone-resume-field"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextInputField
                    name="header.email"
                    label={t('createResume.contact.fieldsEmail')}
                    error={getIn(touched, 'header.email') && getIn(errors, 'header.email')}
                    id="email-resume-field"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextInputField
                    name="header.location"
                    label={t('createResume.contact.fieldsLocation')}
                    error={getIn(touched, 'header.location') && getIn(errors, 'header.location')}
                    placeholder={t('createResume.contact.fieldsLocationPlaceholder')}
                    id="location-resume-field"
                  />
                </Grid>

                <Grid item xs={12} md={12}>
                  <TextInputField
                    name="header.url"
                    label={t('createResume.contact.fieldsPortfolio')}
                    error={getIn(touched, 'header.url') && getIn(errors, 'header.url')}
                    placeholder={t('createResume.contact.fieldsPortfolioPlaceholder')}
                    helperText={t('createResume.contact.fieldsPortfolioHelper')}
                    id="profile-portfolio-resume-field"
                  />
                </Grid>

                {formValues.header.isMilitary && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formValues.header.showMilitaryStatus}
                          onChange={(e) => {
                            const prev = structuredClone(formValues);
                            prev.header.showMilitaryStatus = e.target.checked;

                            prev.sections[1].items = computeSkillsFromSections(prev.sections, e.target.checked);
                            updateFormValues(prev);
                          }}
                          id="military-status-checkbox"
                        />
                      }
                      label={t('createResume.contact.militaryCheckbox')}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          </Form>
        )}
      </Formik>

      {formValues.sections.map((section) => {
        switch (section.type) {
          case 'summary':
            return (
              <Formik
                key={section.type}
                initialValues={{ summary: '' }}
                validationSchema={summarySchema}
                onSubmit={() => {}}
              >
                {({ errors, touched }) => (
                  <Form>
                    <Box mb={2}>
                      <Typography variant="h4" component="h2">
                        {t('createResume.summary.title')}
                      </Typography>

                      <Typography component="p" variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                        {t('createResume.summary.description')}
                      </Typography>

                      <FormControl>
                        <OutlinedInput
                          id="statement-resume-field"
                          aria-labelledby="statement-resume-field"
                          name="summary"
                          value={formValues ? formValues.sections[0].items[0] : ''}
                          onChange={(event) => {
                            const prev = structuredClone(formValues);
                            prev.sections[0].items[0] = event.target.value;
                            updateFormValues(prev);
                          }}
                          multiline
                          inputComponent="textarea"
                          rows={3}
                          error={getIn(touched, 'summary') && getIn(errors, 'summary')}
                        />
                      </FormControl>
                    </Box>
                  </Form>
                )}
              </Formik>
            );
          case 'skills':
            return (
              <Box mb={2} key={section.type}>
                <Typography variant="h4" component="h2">
                  {t('createResume.skills.title')}
                </Typography>

                <Typography component="p" variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                  {t('createResume.skills.description')}
                </Typography>

                <ExperienceWalletSkillsAutocomplete
                  value={formValues ? (formValues.sections[1].items as Experience[]) : []}
                  onChange={(_, newValues) => {
                    const prev = structuredClone(formValues);
                    prev.sections[1].items = newValues;
                    updateFormValues(prev);
                  }}
                  open={skillsOpen}
                  setOpen={setSkillsOpen}
                  id="skills-resume-field"
                />
              </Box>
            );
          case 'work_experience': {
            // Get military items from military_experience section
            const militarySection = formValues.sections.find((s) => s.type === 'military_experience');
            const militaryItems = (militarySection?.items as MilitaryExperience[]) || [];
            const workItems = section.items as Experience[];

            // Combine and tag items with their type and original index
            const combinedItems = [
              ...workItems.map((item, idx) => ({ item, itemType: 'work' as const, originalIndex: idx })),
              ...militaryItems.map((item, idx) => ({ item, itemType: 'military' as const, originalIndex: idx })),
            ].sort((a, b) => compareExperienceDates(a.item, b.item));

            return (
              <Box mb={2} key={section.type}>
                <Typography variant="h4" component="h2">
                  {t('createResume.workExperience.title')}
                </Typography>

                <Typography component="p" variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                  {t('createResume.workExperience.description')}
                </Typography>

                <Stack spacing={1} mb={1}>
                  {combinedItems.map(({ item, itemType, originalIndex }, displayIndex) => {
                    if (itemType === 'military') {
                      const mil = item as MilitaryExperience;
                      const branchLabel =
                        BRANCH_OPTIONS.find((b) => b.value === mil.branch)?.backendName || mil.branch || '';
                      const ranksList = mil.branch
                        ? (RANKS_BY_BRANCH as Record<string, { value: string; label: string }[]>)[mil.branch]
                        : null;
                      const rankLabel = ranksList
                        ? ranksList.find((r) => r.value === mil.rank)?.label || mil.rank || ''
                        : mil.rank || '';
                      const headline = [branchLabel, mil.title].filter(Boolean).join(' - ');
                      return (
                        <EditableCard
                          key={`military-${displayIndex}`}
                          title={headline}
                          subtitle={rankLabel}
                          chipLabel={mil.hidden ? t('createResume.militaryExperience.removedFromResume') : undefined}
                          onEdit={mil.hidden ? undefined : () => handleEditExperience(originalIndex, 'military')}
                          onDelete={
                            mil.hidden
                              ? undefined
                              : () => {
                                  const prev = structuredClone(formValues);
                                  (prev.sections[2].items as MilitaryExperience[])[originalIndex] = {
                                    ...(prev.sections[2].items as MilitaryExperience[])[originalIndex],
                                    hidden: true,
                                  };
                                  prev.sections[1].items = computeSkillsFromSections(
                                    prev.sections,
                                    prev.header.showMilitaryStatus
                                  );
                                  updateFormValues(prev);
                                }
                          }
                          onRestore={
                            mil.hidden
                              ? () => {
                                  const prev = structuredClone(formValues);
                                  (prev.sections[2].items as MilitaryExperience[])[originalIndex] = {
                                    ...(prev.sections[2].items as MilitaryExperience[])[originalIndex],
                                    hidden: false,
                                  };
                                  prev.sections[1].items = computeSkillsFromSections(
                                    prev.sections,
                                    prev.header.showMilitaryStatus
                                  );
                                  updateFormValues(prev);
                                }
                              : undefined
                          }
                        />
                      );
                    } else {
                      const work = item as Experience;
                      return (
                        <EditableCard
                          key={`work-${displayIndex}`}
                          title={work.employer_provider}
                          subtitle={`${work.title || ''}`}
                          chipLabel={work.hidden ? t('createResume.militaryExperience.removedFromResume') : undefined}
                          onEdit={work.hidden ? undefined : () => handleEditExperience(originalIndex, 'work')}
                          onDelete={
                            work.hidden
                              ? undefined
                              : () => {
                                  const prev = structuredClone(formValues);
                                  (prev.sections[3].items as Experience[])[originalIndex] = {
                                    ...(prev.sections[3].items as Experience[])[originalIndex],
                                    hidden: true,
                                  };
                                  prev.sections[1].items = computeSkillsFromSections(
                                    prev.sections,
                                    prev.header.showMilitaryStatus
                                  );
                                  updateFormValues(prev);
                                }
                          }
                          onRestore={
                            work.hidden
                              ? () => {
                                  const prev = structuredClone(formValues);
                                  (prev.sections[3].items as Experience[])[originalIndex] = {
                                    ...(prev.sections[3].items as Experience[])[originalIndex],
                                    hidden: false,
                                  };
                                  prev.sections[1].items = computeSkillsFromSections(
                                    prev.sections,
                                    prev.header.showMilitaryStatus
                                  );
                                  updateFormValues(prev);
                                }
                              : undefined
                          }
                        />
                      );
                    }
                  })}
                </Stack>

                <Button
                  onClick={() => {
                    setExperienceModalOpen(true);
                    setEditingItem({
                      ...EDITING_ITEM_INITIAL_STATE,
                      type: formValues.header.canAddMilitary ? 'military' : 'work',
                    });
                  }}
                  startIcon={<Add />}
                  id="add-experience-resume-button"
                >
                  {t('createResume.workExperience.addButton')}
                </Button>
              </Box>
            );
          }
          case 'others': {
            const sortedEducationItems = section.items
              .map((education, index) => ({ education, originalIndex: index }))
              .sort((a, b) => compareExperienceDates(a.education as Experience, b.education as Experience));

            return (
              <Box mb={2} key={section.type}>
                <Typography variant="h4" component="h2">
                  {t('createResume.education.title')}
                </Typography>

                <Typography component="p" variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                  {t('createResume.education.description')}
                </Typography>

                <Stack spacing={1} mb={1}>
                  {sortedEducationItems.map(({ education, originalIndex }) => {
                    const educationItem = education as Experience;
                    const degreeLevelOption = educationItem.level
                      ? EDUCATION_LEVEL_BY_ID.get(Number(educationItem.level))
                      : null;
                    const educationTypeLabelId = EDUCATION_TYPE_LABEL_BY_KEY.get(educationItem.type);
                    // JST academic degrees store the raw credential code (e.g. "bachDegree") as
                    // the type, so resolve it through the shared credential label map first.
                    const credentialLabelId = (EDUCATION_CREDENTIAL_LABEL_IDS as unknown as Record<string, string>)[
                      educationItem.type
                    ];
                    const educationLabel =
                      educationItem.type === 'degree' && educationItem.level
                        ? degreeLevelOption
                          ? t(degreeLevelOption.labelId)
                          : educationItem.level
                        : credentialLabelId
                        ? t(credentialLabelId)
                        : educationTypeLabelId ||
                          // LCW/DCC-sourced credentials (e.g. "openbadge") carry raw external
                          // type strings that don't map to an i18n key.
                          (lcwTypeLabels as Record<string, string>)[educationItem.type] ||
                          educationItem.type;
                    const handleDeleteEducation = educationItem.hidden
                      ? undefined
                      : () => {
                          const prev = structuredClone(formValues);
                          (prev.sections[4].items as Experience[])[originalIndex] = {
                            ...(prev.sections[4].items as Experience[])[originalIndex],
                            hidden: true,
                          };
                          prev.sections[1].items = computeSkillsFromSections(
                            prev.sections,
                            prev.header.showMilitaryStatus
                          );
                          updateFormValues(prev);
                        };
                    const handleRestoreEducation = educationItem.hidden
                      ? () => {
                          const prev = structuredClone(formValues);
                          (prev.sections[4].items as Experience[])[originalIndex] = {
                            ...(prev.sections[4].items as Experience[])[originalIndex],
                            hidden: false,
                          };
                          prev.sections[1].items = computeSkillsFromSections(
                            prev.sections,
                            prev.header.showMilitaryStatus
                          );
                          updateFormValues(prev);
                        }
                      : undefined;

                    return (
                      <EditableCard
                        key={originalIndex}
                        title={educationLabel + (educationItem.title ? ` - ${educationItem.title}` : '')}
                        subtitle={`${educationItem.employer_provider || ''}${
                          educationItem?.comments ? ' • ' + educationItem.comments : ''
                        }`}
                        chipLabel={
                          educationItem.hidden ? t('createResume.militaryExperience.removedFromResume') : undefined
                        }
                        onEdit={
                          educationItem.hidden ? undefined : () => handleEditExperience(originalIndex, 'education')
                        }
                        onDelete={handleDeleteEducation}
                        onRestore={handleRestoreEducation}
                      />
                    );
                  })}
                </Stack>

                <Button
                  onClick={() => {
                    setExperienceModalOpen(true);
                    setEditingItem({ ...EDITING_ITEM_INITIAL_STATE, type: 'education' });
                  }}
                  startIcon={<Add />}
                  id="add-education-resume-button"
                >
                  {t('createResume.education.addButton')}
                </Button>
              </Box>
            );
          }
          case 'military_experience':
            // Hidden - military items are now displayed in work_experience section
            return null;
          case 'references':
            return (
              <ReferencesSection
                key={section.type}
                section={section}
                formValues={formValues}
                updateFormValues={updateFormValues}
              />
            );

          default:
            return null;
        }
      })}

      <Typography variant="h4" component="h2">
        {t('createResume.footer.title')}
      </Typography>

      <Typography component="p" variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
        <Link sx={{ cursor: 'pointer' }} onClick={handleDownload} id="download-resume-link">
          {t('createResume.footer.downloadLink')}
        </Link>
        {t('createResume.footer.description')}
      </Typography>

      <Dialog open={experienceModalOpen || editingItem.index !== -1} onClose={() => {}}>
        <Box sx={{ px: 3, py: 2, maxWidth: 464, boxSizing: 'border-box' }}>
          <IconButton
            aria-label={t('createResume.modal.closeLabel')}
            onClick={() => {
              setExperienceModalOpen(false);
              setEditingItem(EDITING_ITEM_INITIAL_STATE);
            }}
            sx={{
              position: 'absolute',
              right: 6,
              top: 6,
              color: 'black',
              fontSize: 34,
            }}
          >
            <Close />
          </IconButton>
          <ExperienceWalletForm
            formScope={editingItem.type}
            hasSelectedCategory={editingItem.type === 'military' && editingItem.index !== -1}
            isResumeBuilder={editingItem.type === 'military'}
            showMilitaryCourse={editingItem.type === 'education' && formValues.header.canAddMilitary}
            appendItem={null}
            replaceItem={null}
            currentIndex={editingItem.index}
            experience={
              editingItem.type === 'military'
                ? { ...emptyMilitaryExperience, ...editingItem.item }
                : editingItem.index !== -1
                ? { ...emptyExperience, ...editingItem.item }
                : emptyExperience
            }
            experiences={[]}
            setCurrentContent={closeModal}
            onSubmit={handleFormSubmit}
          />
        </Box>
      </Dialog>
    </Box>
  );
}

export default ResumeEditor;
